import {
  Currency,
  FUNDED_BY_NAME_MAX_LENGTH,
  ImpactGroup,
  ImpactShopProductQuickSelectOption,
  ImpactShopProductType,
  impactShopProductUnitTypePluralMapper,
  isValidEmailAddress,
  penceToDisplayPrice,
  ImpactShopProductState,
  ProductType,
  round,
  currencySymbols,
  getQuantityStep,
  getMaxDecimalPlaces,
} from "@ecologi/shared";
import { isImpactFundProductType } from "@ecologi/shared/src";
import styled from "@emotion/styled";
import { Dispatch, SetStateAction } from "react";
import { Accordion, AccordionItem } from "../Accordion";
import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { Heading } from "../Heading";
import { StatusTag } from "../StatusTag";
import { TextField, TextFieldChangeEvent } from "../TextField";
import { Tooltip } from "../Tooltip";
import { UnitTypeVisualTextMapper } from "../ImpactShopProductCard/ImpactShopProductCard";
import { TagImpactSection } from "./TagImpactSection";
import HelpIcon from "@ecologi/assets/icons/help.svg";

const Outer = styled.div`
  background-color: ${({ theme }) => theme.colors.snowdrop};
  border-left: 1px solid ${({ theme }) => theme.colors.mistLight};

  @media ${({ theme }) => theme.bp.desktopUp} {
    grid-row-start: span 100;
  }
`;

const AddToBasket = styled(Button)`
  margin-bottom: 48px;
`;

const Container = styled.div`
  position: sticky;
  top: 80px;
  background-color: ${({ theme }) => theme.colors.snowdrop};
  padding: 34px 24px 48px 24px;
  font-size: 16px;

  @media ${({ theme }) => theme.bp.wideDesktopUp} {
    padding: 40px 40px;
  }
`;

const FundedBy = styled.div`
  margin-bottom: 24px;
`;

const GiftSection = styled.div`
  margin-bottom: 24px;
`;

const GiftCheckboxLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const GiftHelpLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  background-color: ${({ theme }) => theme.colors.mistDarker};
  border-radius: 50%;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.charcoal};
  }
`;

const GiftHelpIcon = styled(HelpIcon)`
  width: 8px;
  height: 12px;
`;

const GiftEmailInput = styled.div`
  margin-top: 16px;
`;

const ManualQuantity = styled.div`
  margin-bottom: 30px;
`;

const QuickSelect = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
`;

const QuickSelectOption = styled.button<{
  active: boolean;
}>`
  all: unset;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${({ theme }) => theme.colors.mist};
  padding: 24px 6px;
  text-align: center;
  cursor: pointer;
  flex: 1;
  border: 2px solid
    ${(props) =>
      props.active ? props.theme.colors.forest : props.theme.colors.mist};
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  &:focus-visible {
    background: ${({ theme }) => theme.colors.mistDark};
    border-color: ${(props) =>
      props.active ? props.theme.colors.forest : props.theme.colors.mistDark};
  }
  &:hover:enabled {
    background-color: ${(props) =>
      props.active ? props.theme.colors.mist : props.theme.colors.mistDark};
    border: 2px solid
      ${(props) =>
        props.active ? props.theme.colors.forest : props.theme.colors.mistDark};
  }
`;

const QuickSelectOptionLabel = styled(Heading)`
  font-size: 16px !important;
  margin-bottom: 20px;
`;

const QuickSelectOptionPrice = styled.div`
  font-size: 14px;
`;

const SnippetText = styled.p`
  color: ${({ theme }) => theme.colors.charcoalLight};
  margin-bottom: 30px;
`;

const Title = styled(Heading)`
  margin-bottom: 20px;
`;

const UnitPrice = styled.p`
  font-weight: bold;
  margin-bottom: 24px;
  color: ${({ theme }) => theme.colors.charcoalLight};
`;

export type ImpactShopProductPageSidebarProps = {
  currency: Currency;
  groups: ImpactGroup[];
  impactGroupsEnabled: boolean;
  isLoggedIn: boolean;
  onAddToBasket: (
    product: ImpactShopProductType,
    quantity: number,
    fundedBy: string,
    groupId?: number,
    recipientEmail?: string
  ) => void;
  onCreateGroup: (name: string) => Promise<ImpactGroup>;
  onQuickSelectClick: (option: ImpactShopProductQuickSelectOption) => void;
  onSendGiftChange: (isChecked: boolean) => void;
  product: ImpactShopProductType;
  productState: ImpactShopProductState;
  setProductState: Dispatch<SetStateAction<ImpactShopProductState>>;
};

export const ImpactShopProductPageSidebar = ({
  currency,
  groups,
  impactGroupsEnabled,
  isLoggedIn,
  onAddToBasket,
  onCreateGroup,
  onQuickSelectClick,
  onSendGiftChange,
  product,
  productState: {
    quantity,
    activeQuickSelect,
    fundedBy,
    groupId,
    recipientEmail,
  },
  setProductState,
}: ImpactShopProductPageSidebarProps) => {
  const {
    display: { description, impact, snippet, standards, title, unitType },
    isOutOfStock,
    quickSelectOptions,
    maxQuantity,
    minQuantity,
    prices,
    productType,
  } = product;

  const accordionItems: AccordionItem[] = [
    {
      heading: "Description",
      description,
    },
    ...(impact
      ? [
          {
            heading: "Impact",
            description: impact,
          },
        ]
      : []),
    ...(standards
      ? [
          {
            heading: "Certifications",
            description: `This product is verified by one or more of the following standards: ${standards!.join(
              ", "
            )}.`,
          },
        ]
      : []),
  ];

  const handleGroupChange = (id: number | null) => {
    setProductState((prev) => ({ ...prev, groupId: id }));
  };

  const unitPrice = prices.find(
    (price) => price.currency.toLowerCase() === currency.toLowerCase()
  )!.amount;

  const isImpactFundProduct = isImpactFundProductType(productType);
  const hasQuickSelectOptions = quickSelectOptions.length > 0;
  const currencySymbol = currencySymbols[currency];
  const isImpactFundWithUnitType = !!(isImpactFundProduct && unitType);

  const manualQuantityInputLabel = isImpactFundProduct
    ? isImpactFundWithUnitType
      ? undefined
      : `Enter an Impact Fund contribution (${currencySymbol})`
    : `${hasQuickSelectOptions ? "Or enter" : "Enter"} a custom amount`;

  const manualQuantityInputPlaceholder =
    isImpactFundProduct && !isImpactFundWithUnitType
      ? undefined
      : `Minimum ${minQuantity} ${
          unitType
            ? minQuantity > 1
              ? impactShopProductUnitTypePluralMapper[unitType]
              : UnitTypeVisualTextMapper[unitType].text
            : ""
        }`;

  const manualQuantityInputHelpText =
    isImpactFundProduct && !isImpactFundWithUnitType
      ? `The minimum contribution to an Impact Fund is ${currencySymbol}${minQuantity}. This allows us to maximise your impact by meaningfully contributing to each project in the fund cohort.`
      : undefined;

  const isSendingAsGift = recipientEmail !== null;
  const hasRecipientEmailContent = isSendingAsGift && recipientEmail.length > 0;
  const isRecipientEmailValid =
    hasRecipientEmailContent && isValidEmailAddress(recipientEmail);
  const hasValidRecipientEmail = !isSendingAsGift || isRecipientEmailValid;

  const handleSendGiftChange = (isSelected: boolean) => {
    setProductState((prev) => ({
      ...prev,
      recipientEmail: isSelected ? "" : null,
    }));
    onSendGiftChange(isSelected);
  };

  const handleRecipientEmailChange = ({
    target: { value },
  }: TextFieldChangeEvent) => {
    setProductState((prev) => ({ ...prev, recipientEmail: value.trim() }));
  };

  const handleAddToBasket = () => {
    onAddToBasket(
      product,
      quantity ?? 0,
      fundedBy,
      groupId ?? undefined,
      recipientEmail || undefined
    );
  };

  const handleManualQuantityChange = (
    { target: { value } }: TextFieldChangeEvent,
    productType: ProductType
  ) => {
    if (value === "") {
      setProductState((prev) => ({
        ...prev,
        quantity: undefined,
        activeQuickSelect: null,
      }));
      return;
    }

    const numericValue = Number(value);
    if (isNaN(numericValue)) return;

    const maxDecimalPlaces = getMaxDecimalPlaces(
      productType,
      isImpactFundWithUnitType
    );
    const newQuantity = round(numericValue, maxDecimalPlaces);

    setProductState((prev) => ({
      ...prev,
      quantity: newQuantity,
      activeQuickSelect: null,
    }));
  };

  const handleQuickSelect = (
    option: ImpactShopProductQuickSelectOption,
    i: number
  ) => {
    setProductState((prev) => ({
      ...prev,
      quantity: option.quantity,
      activeQuickSelect: i,
    }));
    onQuickSelectClick(option);
  };

  return (
    <Outer>
      <Container>
        <Title as="h1" size={5}>
          {title}
        </Title>
        {isImpactFundProduct ? (
          <>
            <SnippetText>{`${snippet}.`}</SnippetText>
            {isImpactFundWithUnitType && (
              <UnitPrice data-testid="unit-price">
                {penceToDisplayPrice(unitPrice, currency)}/
                {unitType && unitType}
              </UnitPrice>
            )}
          </>
        ) : (
          <UnitPrice data-testid="unit-price">
            {penceToDisplayPrice(unitPrice, currency)}/
            {unitType && UnitTypeVisualTextMapper[unitType].jsx}
          </UnitPrice>
        )}
        {hasQuickSelectOptions ? (
          <QuickSelect data-testid="quick-select">
            {quickSelectOptions.map((option, i) => (
              <QuickSelectOption
                disabled={isOutOfStock}
                active={activeQuickSelect === i}
                key={i}
                onClick={() => !isOutOfStock && handleQuickSelect(option, i)}
              >
                <QuickSelectOptionLabel as="h3">
                  {option.label}
                </QuickSelectOptionLabel>
                <QuickSelectOptionPrice>
                  {penceToDisplayPrice(
                    option.quantity * unitPrice,
                    currency
                  ).replace(".00", "")}
                </QuickSelectOptionPrice>
              </QuickSelectOption>
            ))}
          </QuickSelect>
        ) : null}
        <ManualQuantity>
          <TextField
            data-testid="manual-quantity-input"
            label={manualQuantityInputLabel ?? ""}
            min={minQuantity}
            max={maxQuantity}
            isDisabled={isOutOfStock}
            name="manualQuantity"
            onChange={(e) => handleManualQuantityChange(e, productType)}
            placeholder={manualQuantityInputPlaceholder}
            helpText={manualQuantityInputHelpText}
            step={getQuantityStep(productType, isImpactFundWithUnitType)}
            type="number"
            value={
              quantity === undefined ||
              (quantity === 0 &&
                productType !== ProductType.CARBON_OFFSETS &&
                productType !== ProductType.CARBON_REMOVAL &&
                productType !== ProductType.HABITAT_RESTORATION &&
                !isImpactFundWithUnitType)
                ? ""
                : quantity
            }
          />
        </ManualQuantity>
        <GiftSection>
          <Checkbox
            data-testid="send-gift-checkbox"
            label="Send as gift"
            isSelected={isSendingAsGift}
            isDisabled={isOutOfStock}
            onChange={handleSendGiftChange}
          >
            <GiftCheckboxLabel>
              Send as gift
              <Tooltip content="Learn more about gifting">
                <GiftHelpLink
                  href="https://ecologi-action.notion.site/Gifting-with-the-Impact-shop-2c5edf6c124081e2b153fe8335a3eb5c"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GiftHelpIcon />
                </GiftHelpLink>
              </Tooltip>
              <StatusTag label="New" variant="feature" rounded={false} />
            </GiftCheckboxLabel>
          </Checkbox>
          {isSendingAsGift && (
            <GiftEmailInput>
              <TextField
                data-testid="recipient-email-input"
                label="Recipient email"
                name="recipientEmail"
                type="email"
                isDisabled={isOutOfStock}
                placeholder="Enter the recipient's email address"
                helpText="The recipient will receive an email letting them know you've funded impact on their behalf, with a link to the impact tile on your Ecologi profile."
                value={recipientEmail ?? ""}
                onChange={handleRecipientEmailChange}
                isInvalid={hasRecipientEmailContent && !isRecipientEmailValid}
                errorText={
                  hasRecipientEmailContent && !isRecipientEmailValid
                    ? "Please enter a valid email address"
                    : undefined
                }
              />
            </GiftEmailInput>
          )}
        </GiftSection>
        {impactGroupsEnabled ? (
          <TagImpactSection
            fundedBy={fundedBy}
            groupId={groupId}
            groups={groups}
            isLoggedIn={isLoggedIn}
            isOutOfStock={isOutOfStock}
            onCreateGroup={onCreateGroup}
            onFundedByChange={(value) =>
              setProductState((prev) => ({ ...prev, fundedBy: value }))
            }
            onGroupChange={handleGroupChange}
          />
        ) : (
          <FundedBy>
            <TextField
              data-testid="funded-by-input"
              helpText="This name will be displayed on your impact tile. You can use this to share your impact with your network."
              label="Funded by"
              isDisabled={isOutOfStock}
              name="fundedBy"
              placeholder="Enter an optional 'funded by' tag"
              onChange={({ target: { value: fundedBy } }) =>
                setProductState((prev) => ({ ...prev, fundedBy }))
              }
              maxLength={FUNDED_BY_NAME_MAX_LENGTH}
              showCharacterCount={fundedBy.length > 0}
              value={fundedBy}
            />
          </FundedBy>
        )}
        <AddToBasket
          text={isOutOfStock ? "Out of stock" : "Add to basket"}
          primary
          disabled={
            isOutOfStock ||
            !quantity ||
            quantity < minQuantity ||
            quantity > maxQuantity ||
            fundedBy.length > FUNDED_BY_NAME_MAX_LENGTH ||
            !hasValidRecipientEmail
          }
          fullWidth
          onClick={handleAddToBasket}
        />
        <Accordion items={accordionItems} />
      </Container>
    </Outer>
  );
};
